<script setup lang="ts">
import { computed, onMounted, ref, useRoute, watch } from '#imports'

export interface SaferGamblingQaPanelProps {
    /** Anchor of the Q&A, used when `isQuestion`. */
    id?: string
    /** Gives the panel its anchor and `data-faq-item`, and keeps its answer in the DOM while closed. */
    isQuestion?: boolean
    /** False while an ancestor view hides the panel. */
    active?: boolean
    title: string
    videoId?: string
    videoSrc?: string
    videoPoster?: string
    body: any
    ctas?: Array<{
        label: string
        url: string
        variant?: 'primary' | 'secondary'
    }>
    expandableTextBlocks?: Array<{
        title: string
        content: any
    }>
    showHeader?: boolean
    borderless?: boolean
}

const props = withDefaults(defineProps<SaferGamblingQaPanelProps>(), {
    active: true,
    showHeader: true,
    ctas: () => [],
    expandableTextBlocks: () => [],
    borderless: false
})

const route = useRoute()
const expandable = ref<{ toggle: () => void, open: boolean } | null>(null)

const anchorId = computed(() => props.isQuestion ? props.id : undefined)

function openFromHash() {
    const id = anchorId.value
    if (!id || route.hash !== `#${id}`) return

    if (!expandable.value?.open) expandable.value?.toggle()
    // After the next frame, so an ancestor view switched by the same hash is visible first.
    requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ block: 'start' }))
}

onMounted(openFromHash)
watch(() => route.hash, openFromHash)
</script>

<template>
    <UIExpandable
        :id="anchorId"
        ref="expandable"
        :class="['qa-panel', { 'qa-panel--borderless': props.borderless }]"
        :show-header="props.showHeader"
        :render-closed="props.isQuestion"
        :data-faq-item="props.isQuestion ? '' : undefined"
    >
        <template #header>
            <div class="qa-panel__header">
                {{ title }}
                <UIIcon
                    class="qa-panel__header-icon"
                    name="plus"
                    size="14px"
                />
            </div>
        </template>

        <template #default="{ open }">
            <SaferGamblingQaContent
                :video-id="props.videoId"
                :video-src="props.videoSrc"
                :video-poster="props.videoPoster"
                :video-title="props.title"
                :show-video="open && props.active"
                :body="props.body"
                :ctas="props.ctas"
                :expandable-text-blocks="props.expandableTextBlocks"
            />
        </template>
    </UIExpandable>
</template>

<style scoped lang="scss">
.qa-panel {
    border-bottom: 1px solid rgb(0 0 0 / 12%);

    &--borderless {
        border-bottom: unset;
    }

    &.expandable--open {
        .qa-panel__header-icon {
            transform: rotate(45deg);
        }
    }

    &__header {
        position: relative;
        font-size: 18px;
        color: var(--on-link, $link-color);
        font-weight: 700;
        padding: 15px 0 15px 0;
        display: flex;
        justify-content: space-between;

        &-icon {
            transition: transform 0.2s ease-out;
        }
    }
}
</style>
